'use client';

import { useState, useCallback } from 'react';
import {
  FileText,
  Copy,
  Download,
  Check,
  Building2,
  Globe,
  Shield,
  Users,
  Database,
  Lock,
  AlertCircle
} from 'lucide-react';

// Business Types
type BusinessType =
  | 'ecommerce'
  | 'saas'
  | 'blog'
  | 'mobile-app'
  | 'general-website';

// Compliance Frameworks
type ComplianceFramework = 'gdpr' | 'ccpa' | 'caloppa' | 'coppa' | 'pipeda';

interface BusinessConfig {
  type: BusinessType;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  defaultCollections: string[];
}

interface DataCollection {
  id: string;
  name: string;
  description: string;
  examples: string;
  required?: boolean;
}

interface PolicyConfig {
  businessName: string;
  websiteUrl: string;
  businessType: BusinessType;
  contactEmail: string;
  effectiveDate: string;
  country: string;
  dataCollections: string[];
  complianceFrameworks: ComplianceFramework[];
  cookiesUsed: boolean;
  thirdPartyServices: string[];
  childrenUnder13: boolean;
  dataRetentionPeriod: string;
  securityMeasures: string[];
}

const BUSINESS_TYPES: BusinessConfig[] = [
  {
    type: 'ecommerce',
    name: 'E-commerce Store',
    icon: Building2,
    description: 'Online stores selling products or services',
    defaultCollections: ['personal-info', 'payment-info', 'shipping-info', 'order-history', 'cookies']
  },
  {
    type: 'saas',
    name: 'SaaS Application',
    icon: Globe,
    description: 'Software-as-a-Service platforms',
    defaultCollections: ['personal-info', 'account-info', 'usage-data', 'payment-info', 'cookies']
  },
  {
    type: 'blog',
    name: 'Blog / Content Site',
    icon: FileText,
    description: 'Blogs, news sites, content platforms',
    defaultCollections: ['personal-info', 'comments', 'analytics', 'cookies']
  },
  {
    type: 'mobile-app',
    name: 'Mobile Application',
    icon: Users,
    description: 'iOS or Android mobile apps',
    defaultCollections: ['personal-info', 'device-info', 'location-data', 'usage-data']
  },
  {
    type: 'general-website',
    name: 'General Website',
    icon: Globe,
    description: 'Business websites, portfolios, landing pages',
    defaultCollections: ['personal-info', 'contact-forms', 'analytics', 'cookies']
  }
];

const DATA_COLLECTIONS: DataCollection[] = [
  { id: 'personal-info', name: 'Personal Information', description: 'Names, email addresses, phone numbers', examples: 'Name, email, phone number, address', required: true },
  { id: 'payment-info', name: 'Payment Information', description: 'Credit cards, billing addresses', examples: 'Credit card numbers, billing addresses, payment processor tokens' },
  { id: 'shipping-info', name: 'Shipping Information', description: 'Delivery addresses and preferences', examples: 'Shipping address, delivery instructions' },
  { id: 'account-info', name: 'Account Information', description: 'Usernames, passwords, profile data', examples: 'Username, password hash, profile picture, preferences' },
  { id: 'order-history', name: 'Order/Transaction History', description: 'Purchase records and invoices', examples: 'Past orders, transaction records, receipts' },
  { id: 'usage-data', name: 'Usage Data', description: 'How users interact with your service', examples: 'Feature usage, time spent, actions taken' },
  { id: 'device-info', name: 'Device Information', description: 'Device identifiers and specifications', examples: 'Device ID, OS version, browser type, screen resolution' },
  { id: 'location-data', name: 'Location Data', description: 'Geographic location information', examples: 'GPS coordinates, IP-based location, timezone' },
  { id: 'cookies', name: 'Cookies & Tracking', description: 'Browser cookies and similar technologies', examples: 'Session cookies, persistent cookies, pixel tags' },
  { id: 'analytics', name: 'Analytics Data', description: 'Website/app usage statistics', examples: 'Page views, click paths, session duration' },
  { id: 'contact-forms', name: 'Contact Form Submissions', description: 'Messages sent through contact forms', examples: 'Inquiry messages, feedback, support requests' },
  { id: 'comments', name: 'User Comments', description: 'Blog comments and user-generated content', examples: 'Blog comments, reviews, forum posts' },
  { id: 'health-data', name: 'Health Information', description: 'Medical or health-related data', examples: 'Health records, fitness data, medical history' },
  { id: 'financial-data', name: 'Financial Information', description: 'Bank accounts, income, financial status', examples: 'Bank account numbers, income data, credit scores' },
  { id: 'social-media', name: 'Social Media Data', description: 'Data from social media integrations', examples: 'Social profiles, friends lists, posts' }
];

const THIRD_PARTY_SERVICES = [
  'Google Analytics',
  'Facebook Pixel',
  'Stripe',
  'PayPal',
  'Mailchimp',
  'Intercom',
  'Zendesk',
  'AWS',
  'Cloudflare',
  'HubSpot',
  'Salesforce',
  'Mixpanel',
  'Hotjar',
  'Segment',
  'Twilio'
];

const SECURITY_MEASURES = [
  'SSL/TLS encryption',
  'Data encryption at rest',
  'Secure password hashing',
  'Two-factor authentication',
  'Regular security audits',
  'Access controls and logging',
  'Firewall protection',
  'DDoS protection',
  'Intrusion detection',
  'Employee security training'
];

const COMPLIANCE_FRAMEWORKS: { id: ComplianceFramework; name: string; description: string; regions: string }[] = [
  { id: 'gdpr', name: 'GDPR', description: 'General Data Protection Regulation', regions: 'European Union' },
  { id: 'ccpa', name: 'CCPA/CPRA', description: 'California Consumer Privacy Act', regions: 'California, USA' },
  { id: 'caloppa', name: 'CalOPPA', description: 'California Online Privacy Protection Act', regions: 'California, USA' },
  { id: 'coppa', name: 'COPPA', description: "Children's Online Privacy Protection Act", regions: 'USA (children under 13)' },
  { id: 'pipeda', name: 'PIPEDA', description: 'Personal Information Protection Act', regions: 'Canada' }
];

const generatePrivacyPolicy = (config: PolicyConfig): string => {
  const today = config.effectiveDate || new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const selectedDataCollections = DATA_COLLECTIONS.filter(dc => config.dataCollections.includes(dc.id));

  let policy = `PRIVACY POLICY

Last Updated: ${today}

Welcome to ${config.businessName}. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website${config.websiteUrl ? ` (${config.websiteUrl})` : ''} and use our services.

Please read this privacy policy carefully. By using our services, you consent to the practices described in this policy.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. INFORMATION WE COLLECT

We may collect information about you in various ways, including:

`;

  // Personal Information section
  if (selectedDataCollections.length > 0) {
    policy += `Personal Data You Provide:
`;
    selectedDataCollections.forEach(dc => {
      policy += `• ${dc.name}: ${dc.examples}
`;
    });
    policy += `
`;
  }

  // Automatic collection
  if (config.dataCollections.includes('analytics') || config.dataCollections.includes('device-info') || config.dataCollections.includes('cookies')) {
    policy += `Automatically Collected Information:
When you access our services, we may automatically collect certain information, including:
• Log data (IP address, browser type, pages visited, time and date)
• Device information (device type, operating system, unique identifiers)
• Usage patterns and preferences
`;
    if (config.dataCollections.includes('location-data')) {
      policy += `• Location data (if you grant permission)
`;
    }
    policy += `
`;
  }

  // Cookies section
  if (config.cookiesUsed || config.dataCollections.includes('cookies')) {
    policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

2. COOKIES AND TRACKING TECHNOLOGIES

We use cookies and similar tracking technologies to enhance your experience:

• Essential Cookies: Required for basic site functionality
• Performance Cookies: Help us understand how visitors interact with our site
• Functional Cookies: Remember your preferences and settings
• Marketing Cookies: Used to deliver relevant advertisements

You can control cookies through your browser settings. However, disabling certain cookies may limit your use of some features.

`;
  }

  // How we use information
  policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${config.cookiesUsed ? '3' : '2'}. HOW WE USE YOUR INFORMATION

We use the information we collect for purposes including:

• Providing and maintaining our services
• Processing transactions and sending related information
• Sending promotional communications (with your consent)
• Analyzing usage to improve our services
• Protecting against fraudulent or illegal activity
• Complying with legal obligations
• Responding to your comments and questions

`;

  // Third-party sharing
  if (config.thirdPartyServices.length > 0) {
    policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${config.cookiesUsed ? '4' : '3'}. THIRD-PARTY SERVICES

We may share your information with trusted third-party service providers:

`;
    config.thirdPartyServices.forEach(service => {
      policy += `• ${service}
`;
    });
    policy += `
These providers are bound by contractual obligations to keep personal information confidential and use it only for the purposes for which we disclose it to them.

`;
  }

  // Data retention
  policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${config.thirdPartyServices.length > 0 ? (config.cookiesUsed ? '5' : '4') : (config.cookiesUsed ? '4' : '3')}. DATA RETENTION

We retain your personal information for ${config.dataRetentionPeriod || 'as long as necessary to fulfill the purposes outlined in this policy'}, unless a longer retention period is required by law.

`;

  // Security
  if (config.securityMeasures.length > 0) {
    policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

DATA SECURITY

We implement appropriate technical and organizational measures to protect your information:

`;
    config.securityMeasures.forEach(measure => {
      policy += `• ${measure}
`;
    });
    policy += `
While we strive to protect your personal information, no method of transmission over the Internet is 100% secure.

`;
  }

  // GDPR specific rights
  if (config.complianceFrameworks.includes('gdpr')) {
    policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

YOUR RIGHTS UNDER GDPR (European Economic Area Residents)

If you are a resident of the European Economic Area, you have certain data protection rights:

• Right to Access: Request copies of your personal data
• Right to Rectification: Request correction of inaccurate data
• Right to Erasure: Request deletion of your personal data
• Right to Restrict Processing: Request limitation of how we use your data
• Right to Data Portability: Request transfer of your data to another service
• Right to Object: Object to our processing of your personal data
• Right to Withdraw Consent: Withdraw consent at any time where we relied on consent

To exercise these rights, contact us at ${config.contactEmail}.

Legal Basis for Processing:
• Consent: Where you have given explicit consent
• Contract: Where processing is necessary to perform a contract with you
• Legal Obligation: Where processing is required by law
• Legitimate Interests: Where we have a legitimate business interest

`;
  }

  // CCPA specific rights
  if (config.complianceFrameworks.includes('ccpa')) {
    policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

YOUR RIGHTS UNDER CCPA (California Residents)

If you are a California resident, you have specific rights regarding your personal information:

• Right to Know: Request disclosure of categories and specific pieces of personal information collected
• Right to Delete: Request deletion of personal information (with some exceptions)
• Right to Opt-Out: Opt out of the sale of personal information
• Right to Non-Discrimination: We will not discriminate against you for exercising your rights

To submit a request, contact us at ${config.contactEmail}.

We do not sell personal information as defined under CCPA.

`;
  }

  // COPPA compliance
  if (config.complianceFrameworks.includes('coppa') || config.childrenUnder13) {
    policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

CHILDREN'S PRIVACY

Our services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us at ${config.contactEmail}. If we discover we have collected personal information from a child under 13, we will delete it immediately.

`;
  }

  // International transfers
  policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

INTERNATIONAL DATA TRANSFERS

Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws different from your country. We take appropriate safeguards to ensure your information remains protected in accordance with this policy.

`;

  // Policy changes
  policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

CHANGES TO THIS POLICY

We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically.

`;

  // Contact information
  policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

CONTACT US

If you have questions about this Privacy Policy or our privacy practices, please contact us:

${config.businessName}
Email: ${config.contactEmail}
${config.websiteUrl ? `Website: ${config.websiteUrl}` : ''}

`;

  if (config.complianceFrameworks.includes('gdpr')) {
    policy += `For EU residents, you also have the right to lodge a complaint with your local supervisory authority.

`;
  }

  policy += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

© ${new Date().getFullYear()} ${config.businessName}. All rights reserved.
`;

  return policy;
};

export default function PrivacyPolicyGeneratorClient() {
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState<PolicyConfig>({
    businessName: '',
    websiteUrl: '',
    businessType: 'general-website',
    contactEmail: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    country: 'United States',
    dataCollections: ['personal-info', 'contact-forms', 'analytics', 'cookies'],
    complianceFrameworks: [],
    cookiesUsed: true,
    thirdPartyServices: [],
    childrenUnder13: false,
    dataRetentionPeriod: '2 years after account closure',
    securityMeasures: ['SSL/TLS encryption', 'Secure password hashing', 'Access controls and logging']
  });
  const [generatedPolicy, setGeneratedPolicy] = useState('');
  const [copied, setCopied] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['basic', 'data', 'compliance']);

  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const handleBusinessTypeChange = (type: BusinessType) => {
    const businessConfig = BUSINESS_TYPES.find(b => b.type === type);
    if (businessConfig) {
      setConfig(prev => ({
        ...prev,
        businessType: type,
        dataCollections: businessConfig.defaultCollections
      }));
    }
  };

  const toggleDataCollection = (id: string) => {
    setConfig(prev => ({
      ...prev,
      dataCollections: prev.dataCollections.includes(id)
        ? prev.dataCollections.filter(d => d !== id)
        : [...prev.dataCollections, id]
    }));
  };

  const toggleComplianceFramework = (id: ComplianceFramework) => {
    setConfig(prev => ({
      ...prev,
      complianceFrameworks: prev.complianceFrameworks.includes(id)
        ? prev.complianceFrameworks.filter(f => f !== id)
        : [...prev.complianceFrameworks, id]
    }));
  };

  const toggleThirdPartyService = (service: string) => {
    setConfig(prev => ({
      ...prev,
      thirdPartyServices: prev.thirdPartyServices.includes(service)
        ? prev.thirdPartyServices.filter(s => s !== service)
        : [...prev.thirdPartyServices, service]
    }));
  };

  const toggleSecurityMeasure = (measure: string) => {
    setConfig(prev => ({
      ...prev,
      securityMeasures: prev.securityMeasures.includes(measure)
        ? prev.securityMeasures.filter(m => m !== measure)
        : [...prev.securityMeasures, measure]
    }));
  };

  const generatePolicy = useCallback(() => {
    const policy = generatePrivacyPolicy(config);
    setGeneratedPolicy(policy);
    setStep(4);
  }, [config]);

  const copyToClipboard = useCallback(async () => {
    await navigator.clipboard.writeText(generatedPolicy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [generatedPolicy]);

  const downloadPolicy = useCallback(() => {
    const blob = new Blob([generatedPolicy], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `privacy-policy-${config.businessName.toLowerCase().replace(/\s+/g, '-') || 'document'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [generatedPolicy, config.businessName]);

  const isBasicInfoComplete = config.businessName && config.contactEmail;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <button
                onClick={() => s < step && setStep(s)}
                disabled={s > step}
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  s === step
                    ? 'bg-[#2813e8] text-white'
                    : s < step
                    ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s < step ? <Check className="w-5 h-5" /> : s}
              </button>
              {s < 4 && (
                <div className={`w-16 sm:w-24 h-1 mx-2 ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-sm text-gray-600">
          <span>Business Info</span>
          <span>Data Collection</span>
          <span>Compliance</span>
          <span>Generate</span>
        </div>
      </div>

      {/* Step 1: Business Information */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#2813e8]" />
              Business Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={config.businessName}
                  onChange={(e) => setConfig(prev => ({ ...prev, businessName: e.target.value }))}
                  placeholder="Your Company Name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website URL
                </label>
                <input
                  type="url"
                  value={config.websiteUrl}
                  onChange={(e) => setConfig(prev => ({ ...prev, websiteUrl: e.target.value }))}
                  placeholder="https://example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={config.contactEmail}
                  onChange={(e) => setConfig(prev => ({ ...prev, contactEmail: e.target.value }))}
                  placeholder="privacy@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Policy Effective Date
                </label>
                <input
                  type="date"
                  value={config.effectiveDate}
                  onChange={(e) => setConfig(prev => ({ ...prev, effectiveDate: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Type
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {BUSINESS_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.type}
                        onClick={() => handleBusinessTypeChange(type.type)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          config.businessType === type.type
                            ? 'border-[#2813e8] bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 mb-1 ${config.businessType === type.type ? 'text-[#2813e8]' : 'text-gray-500'}`} />
                        <div className="text-sm font-medium text-gray-800">{type.name}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setStep(2)}
              disabled={!isBasicInfoComplete}
              className="px-6 py-2 bg-[#2813e8] text-white rounded-lg hover:bg-[#1f0fb3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Data Collection →
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Data Collection */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Database className="w-6 h-6 text-[#2813e8]" />
              Data Collection Practices
            </h2>
            <p className="text-gray-600 mb-4">Select all types of data your website or app collects:</p>

            <div className="grid sm:grid-cols-2 gap-3">
              {DATA_COLLECTIONS.map((dc) => (
                <button
                  key={dc.id}
                  onClick={() => toggleDataCollection(dc.id)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    config.dataCollections.includes(dc.id)
                      ? 'border-[#2813e8] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      config.dataCollections.includes(dc.id)
                        ? 'bg-[#2813e8] border-[#2813e8]'
                        : 'border-gray-300'
                    }`}>
                      {config.dataCollections.includes(dc.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="font-medium text-gray-800">{dc.name}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-7">{dc.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Third-party services */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#2813e8]" />
              Third-Party Services
            </h3>
            <p className="text-gray-600 mb-4 text-sm">Select services you use that process user data:</p>
            <div className="flex flex-wrap gap-2">
              {THIRD_PARTY_SERVICES.map((service) => (
                <button
                  key={service}
                  onClick={() => toggleThirdPartyService(service)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    config.thirdPartyServices.includes(service)
                      ? 'bg-[#2813e8] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {service}
                </button>
              ))}
            </div>
          </div>

          {/* Security measures */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#2813e8]" />
              Security Measures
            </h3>
            <p className="text-gray-600 mb-4 text-sm">Select security measures you implement:</p>
            <div className="flex flex-wrap gap-2">
              {SECURITY_MEASURES.map((measure) => (
                <button
                  key={measure}
                  onClick={() => toggleSecurityMeasure(measure)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    config.securityMeasures.includes(measure)
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {measure}
                </button>
              ))}
            </div>
          </div>

          {/* Additional options */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Additional Options</h3>

            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.cookiesUsed}
                  onChange={(e) => setConfig(prev => ({ ...prev, cookiesUsed: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-[#2813e8] focus:ring-[#2813e8]"
                />
                <div>
                  <span className="font-medium text-gray-800">Uses Cookies</span>
                  <p className="text-sm text-gray-500">Include detailed cookie policy section</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.childrenUnder13}
                  onChange={(e) => setConfig(prev => ({ ...prev, childrenUnder13: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-[#2813e8] focus:ring-[#2813e8]"
                />
                <div>
                  <span className="font-medium text-gray-800">May Have Users Under 13</span>
                  <p className="text-sm text-gray-500">Include COPPA compliance section</p>
                </div>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Retention Period
                </label>
                <input
                  type="text"
                  value={config.dataRetentionPeriod}
                  onChange={(e) => setConfig(prev => ({ ...prev, dataRetentionPeriod: e.target.value }))}
                  placeholder="e.g., 2 years after account closure"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ← Back
            </button>
            <button
              onClick={() => setStep(3)}
              className="px-6 py-2 bg-[#2813e8] text-white rounded-lg hover:bg-[#1f0fb3] transition-colors"
            >
              Next: Compliance →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Compliance Frameworks */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#2813e8]" />
              Compliance Frameworks
            </h2>
            <p className="text-gray-600 mb-4">
              Select the privacy regulations that apply to your business. This will add specific sections to comply with these laws.
            </p>

            <div className="space-y-3">
              {COMPLIANCE_FRAMEWORKS.map((framework) => (
                <button
                  key={framework.id}
                  onClick={() => toggleComplianceFramework(framework.id)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    config.complianceFrameworks.includes(framework.id)
                      ? 'border-[#2813e8] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          config.complianceFrameworks.includes(framework.id)
                            ? 'bg-[#2813e8] border-[#2813e8]'
                            : 'border-gray-300'
                        }`}>
                          {config.complianceFrameworks.includes(framework.id) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="font-semibold text-gray-800">{framework.name}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 ml-7">{framework.description}</p>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{framework.regions}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Legal Disclaimer:</strong> This generator creates a privacy policy template based on common practices.
                It is not legal advice. We strongly recommend having your privacy policy reviewed by a qualified attorney
                familiar with data protection laws applicable to your business.
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(2)}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ← Back
            </button>
            <button
              onClick={generatePolicy}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <FileText className="w-5 h-5" />
              Generate Privacy Policy
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Generated Policy */}
      {step === 4 && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FileText className="w-6 h-6 text-green-600" />
                Your Privacy Policy
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-2 px-4 py-2 bg-[#2813e8] text-white rounded-lg hover:bg-[#1f0fb3] transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={downloadPolicy}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-h-[500px] overflow-y-auto">
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700">{generatedPolicy}</pre>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Next Steps</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Review the generated policy and customize it to fit your specific practices</li>
              <li>• Have the policy reviewed by a legal professional</li>
              <li>• Add the policy to your website (usually in the footer)</li>
              <li>• Link to the policy from your sign-up forms and checkout pages</li>
              <li>• Set up a process to notify users of policy updates</li>
            </ul>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(3)}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ← Edit Settings
            </button>
            <button
              onClick={() => {
                setStep(1);
                setGeneratedPolicy('');
              }}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Start Over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
